import React, { useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

const isValidRedirect = (candidate: string | null): string | null => {
  if (!candidate) {
    return null;
  }

  let decodedCandidate = candidate;
  try {
    decodedCandidate = decodeURIComponent(candidate);
  } catch {
    decodedCandidate = candidate;
  }

  try {
    const parsed = new URL(decodedCandidate);
    if (parsed.protocol === "http:" || parsed.protocol === "https:") {
      return parsed.href;
    }
    return null;
  } catch {
    return null;
  }
};

const Unsubscribe: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const redirectUrl = useMemo(() => {
    const redirectParam = searchParams.get("redirect");
    return isValidRedirect(redirectParam);
  }, [searchParams]);

  const handleNeverMind = () => {
    navigate("/");
  };

  const handleUnsubscribe = () => {
    if (redirectUrl) {
      window.location.href = redirectUrl;
    } else {
      navigate("/");
    }
  };

  return (
    <div className="min-h-screen bg-[#FCF8F5] flex flex-col">
      <header className="w-full py-6 flex justify-center">
        <img
          src="/assets/images/logo.png"
          alt="Bookish Boyfriend"
          className="h-12"
        />
      </header>

      <main className="flex-1 flex items-center justify-center px-4">
        <div className="w-full max-w-xl bg-white shadow-2xl rounded-3xl p-8 text-center">
          <p className="text-sm font-semibold text-[#C17A5C] tracking-wide uppercase mb-3">
            Manage character emails
          </p>
          <h1 className="text-3xl font-bold mb-4 text-[#381A21]">
            Are you sure you want to unsubscribe?
          </h1>
          <p className="text-base text-[#6B4E57] leading-relaxed">
            You&apos;ll stop receiving new story updates, emails from the
            characters you like. You can change your mind anytime inside the
            website.
          </p>

          {!redirectUrl && (
            <p className="mt-6 text-sm text-red-600">
              We couldn&apos;t locate the unsubscribe link. You can still manage
              your email preferences from Settings &rarr; Personal Information
              after logging in.
            </p>
          )}

          <div className="mt-10 flex flex-col gap-4 sm:flex-row sm:justify-center">
            <button
              type="button"
              onClick={handleNeverMind}
              className="w-full sm:w-auto px-6 py-3 rounded-full border border-[#C17A5C] text-[#C17A5C] font-semibold hover:bg-[#FFF7F2] transition"
            >
              Never mind
            </button>
            <button
              type="button"
              onClick={handleUnsubscribe}
              disabled={!redirectUrl}
              className={`w-full sm:w-auto px-6 py-3 rounded-full font-semibold text-white transition ${
                redirectUrl
                  ? "bg-[#EA5E5E] hover:bg-[#d94c4c]"
                  : "bg-gray-400 cursor-not-allowed"
              }`}
            >
              Unsubscribe
            </button>
          </div>

          <p className="mt-8 text-xs text-[#8B6F76]">
            You can resubscribe anytime from your account settings.
          </p>
        </div>
      </main>
    </div>
  );
};

export default Unsubscribe;
